import { useState, useEffect, useRef, type ReactNode } from 'react';

const CITY_IMAGES = [
  '/cities/new-york.jpg',
  '/cities/london.jpg',
  '/cities/tokyo.jpg',
  '/cities/san-francisco.jpg',
  '/cities/hong-kong.jpg',
  '/cities/sydney.jpg',
  '/cities/denver.jpg',
  '/cities/seoul.jpg',
  '/cities/dubai.jpg',
  '/cities/istanbul.jpg',
  '/cities/rome.jpg',
  '/cities/paris.jpg',
  '/cities/barcelona.jpg',
  '/cities/singapore.jpg',
  '/cities/chicago.jpg',
  '/cities/moscow.jpg',
  '/cities/shanghai.jpg',
  '/cities/los-angeles.jpg',
  '/cities/washington-dc.jpg',
  '/cities/toronto.jpg',
  '/cities/mexico-city.jpg',
  '/cities/rio.jpg',
  '/cities/cape-town.jpg',
  '/cities/berlin.jpg',
  '/cities/amsterdam.jpg',
  '/cities/lisbon.jpg',
  '/cities/athens.jpg',
  '/cities/bangkok.jpg',
  '/cities/kyoto.jpg',
  '/cities/buenos-aires.jpg',
  '/cities/kuala-lumpur.jpg',
  '/cities/stockholm.jpg',
  '/cities/vienna.jpg',
  '/cities/zurich.jpg',
  '/cities/prague.jpg',
  '/cities/bogota.jpg',
  '/cities/budapest.jpg',
  '/cities/cairo.jpg',
  '/cities/doha.jpg',
  '/cities/dublin.jpg',
  '/cities/edinburgh.jpg',
  '/cities/helsinki.jpg',
  '/cities/jakarta.jpg',
  '/cities/johannesburg.jpg',
  '/cities/las-vegas.jpg',
  '/cities/lima.jpg',
  '/cities/manila.jpg',
  '/cities/marseille.jpg',
  '/cities/milan.jpg',
  '/cities/nairobi.jpg',
  '/cities/osaka.jpg',
  '/cities/santiago.jpg',
  '/cities/seattle.jpg',
  '/cities/taipei.jpg',
  '/cities/vancouver.jpg',
  '/cities/warsaw.jpg',
];

/** Rotation interval — 10s dev, 30s production */
const ROTATE_MS = import.meta.env.DEV ? 10_000 : 30_000;
const FADE_MS = 1000;

interface PageBannerProps {
  children: ReactNode;
}

export function PageBanner({ children }: PageBannerProps) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * CITY_IMAGES.length));
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [showNext, setShowNext] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    timerRef.current = setInterval(() => {
      const next = (index + 1) % CITY_IMAGES.length;
      setNextIndex(next);
      // Trigger fade after next image loads
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setShowNext(true));
      });
      // After fade completes, swap
      setTimeout(() => {
        setIndex(next);
        setNextIndex(null);
        setShowNext(false);
      }, FADE_MS + 50);
    }, ROTATE_MS);

    return () => clearInterval(timerRef.current);
  });

  return (
    <div className="relative rounded-xl overflow-hidden">
      {/* Current image */}
      <img
        src={CITY_IMAGES[index]}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: 1 }}
      />
      {/* Next image (fades in on top) */}
      {nextIndex !== null && (
        <img
          src={CITY_IMAGES[nextIndex]}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: showNext ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease-in-out`,
          }}
        />
      )}
      {/* Light overlay for readability — cities stay visible */}
      <div className="absolute inset-0 bg-background/15" />
      <div className="relative z-10 p-5 space-y-3 font-bold [&_*]:!font-bold">
        {children}
      </div>
    </div>
  );
}
